"use client";

import React, { useEffect, useRef, useState } from "react";
import { Wind, User, Activity, Sun, Shield, Brain } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefitsData = [{
  id: 1,
  title: "Innere Ruhe",
  description: "Reduziert Stress, beruhigt das Nervensystem und senkt den Cortisolspiegel.",
  iconName: "Wind"
}, {
  id: 2,
  title: "Bessere Haltung",
  description: "Stärkt die Rumpfmuskulatur und verbessert die Körperhaltung nachhaltig.",
  iconName: "User"
}, {
  id: 3,
  title: "Mehr Flexibilität",
  description: "Löst Verspannungen und erhöht die Beweglichkeit von Gelenken und Muskeln.",
  iconName: "Activity"
}, {
  id: 4,
  title: "Gesteigerte Energie",
  description: "Aktiviert den Energiefluss (Qi) und fördert Vitalität und Lebensfreude.",
  iconName: "Sun"
}, {
  id: 5,
  title: "Stärkeres Immunsystem",
  description: "Unterstützt die körpereigenen Abwehrkräfte und verbessert die Organfunktion.",
  iconName: "Shield"
}, {
  id: 6,
  title: "Mentale Klarheit",
  description: "Fördert Konzentrationsfähigkeit, Präsenz und geistige Frische.",
  iconName: "Brain"
}];
const iconMap: Record<string, React.ElementType> = {
  Wind,
  User,
  Activity,
  Sun,
  Shield,
  Brain
};
export default function Benefits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <>
      <section id="benefits" className="bg-background py-20 md:py-24 px-4 md:px-8">
        <div ref={ref} className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-light text-primary uppercase tracking-tight text-center mb-16">Positive Wirkungen von Tai Chi</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefitsData.map((benefit, i) => {
            const IconComponent = iconMap[benefit.iconName];
            return <Card key={benefit.id} data-index={i} className="flex flex-col items-center text-center border-border shadow-sm hover:shadow-md transition-shadow duration-300 pt-8">
                  <CardHeader className="pb-4 flex flex-col items-center gap-4">
                    {IconComponent && <IconComponent className="w-8 h-8 text-primary" strokeWidth={1.5} />}
                    <CardTitle className="text-lg font-normal text-card-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base font-light text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      
    </>;
}